import { RefObject, useLayoutEffect, useRef, useState } from "react";
import { Button, IconButton, SvgIcon, Tooltip } from "@mui/material";
import i18n from "../i18n";
import styles from "./event-banner.module.css";
import { EventBanner as EventBannerType } from "../types/general-types";
import ArrowDiagonal from "../images/portal/arrow-diagonal.svg?react";
import CaretDown from "../images/portal/caret-down.svg?react";
import CaretUp from "../images/portal/caret-up.svg?react";
import Close from "../images/portal/close-cross.svg?react";
import starsStart from "../images/portal/stars-start.svg";
import starsEnd from "../images/portal/stars-end.svg";

type Direction = "next" | "prev" | undefined;

type BannerContentProps = {
	banner: EventBannerType;
	type: string;
	direction: Direction;
	showMultipleBanners: boolean;
	wrapperRef: RefObject<HTMLDivElement>;
};

type EventBannerProps = {
	banners: EventBannerType[];
	onClose?: () => void;
};

const normalizeLink = (link: string) =>
	link.startsWith("http://") || link.startsWith("https://") ? link : `https://${link}`;

const BannerContent = ({ banner, type, direction, showMultipleBanners, wrapperRef }: BannerContentProps) => {
	const [starsVisible, setStarsVisible] = useState(true);
	const contentRef = useRef<HTMLDivElement>(null);
	const bannerClass =
		type === "current" ? styles.currentBanner : type === "prev" ? styles.prevBanner : styles.nextBanner;
	const movingClass =
		direction !== undefined
			? direction === "prev"
				? `${styles.moving} ${styles.movingPrev}`
				: `${styles.moving} ${styles.movingNext}`
			: "";

	useLayoutEffect(() => {
		if (!wrapperRef.current || !contentRef.current) return;

		const calculateSpace = () => {
			if (!wrapperRef.current || !contentRef.current) return;

			const starWidthNeeded = 200;
			const wrapperWidth = wrapperRef.current!.clientWidth;
			const contentWidth = contentRef.current!.offsetWidth;
			const newVisibility = wrapperWidth - contentWidth >= starWidthNeeded;
			setStarsVisible(newVisibility);
		};

		calculateSpace();
		const observer = new ResizeObserver(calculateSpace);
		observer.observe(wrapperRef.current);
		return () => observer.disconnect();
	}, [banner]);

	return (
		<div
			className={`${styles.bannerContent} ${bannerClass} ${movingClass} ${!starsVisible ? styles.wrapText : undefined}`}
		>
			{starsVisible && (
				<img
					role="presentation"
					alt=""
					src={starsStart}
					draggable="false"
					className={`${styles.starsStart} ${!showMultipleBanners ? styles.starsStartNoCarets : ""}`}
				/>
			)}
			<div ref={contentRef} className={styles.contentWrapper}>
				<div className={styles.bannerText}>
					<span className={styles.bannerTitle}>{banner.title}</span>
				</div>
				<Button
					component="a"
					className={styles.bannerButton}
					href={normalizeLink(banner.link)}
					target="_blank"
					rel="noopener noreferrer"
					disableFocusRipple
					disableTouchRipple
				>
					{banner.buttonText}
					<ArrowDiagonal />
				</Button>
			</div>
			{starsVisible && (
				<img
					role="presentation"
					alt=""
					src={starsEnd}
					draggable="false"
					className={styles.starsEnd}
				/>
			)}
		</div>
	);
};

export default function EventBanner({ banners, onClose }: EventBannerProps) {
	const [index, setIndex] = useState(0);
	const [direction, setDirection] = useState<Direction>(undefined);
	const wrapperRef = useRef<HTMLDivElement>(null);

	if (!banners || banners.length === 0) return null;

	const showMultipleBanners = banners.length >= 2;
	const nextIndex = (index + 1) % banners.length;
	const prevIndex = (index - 1 + banners.length) % banners.length;
	const bannersToRender = !showMultipleBanners
		? [{ index: index, type: "current" }]
		: [
				{ index: nextIndex, type: "next" },
				{ index: index, type: "current" },
				{ index: prevIndex, type: "prev" },
			];

	const handlePrev = () => {
		if (direction) return;
		setDirection("prev");

		setTimeout(() => {
			setIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
			setDirection(undefined);
		}, 500);
	};

	const handleNext = () => {
		if (direction) return;
		setDirection("next");

		setTimeout(() => {
			setIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
			setDirection(undefined);
		}, 500);
	};

	return (
		<div
			role="region"
			aria-label={i18n.t("portal.components.eventBannerTSX.ariaLabel")}
			className={`${styles.eventBanner} ${!showMultipleBanners ? styles.eventBannerNoCarets : undefined}`}
		>
			{showMultipleBanners && (
				<div className={styles.navigationButtons}>
					<Tooltip title={i18n.t("portal.components.eventBannerTSX.previousButton")}>
						<IconButton className={styles.navigationButton} onClick={handlePrev}>
							<SvgIcon>
								<CaretUp />
							</SvgIcon>
						</IconButton>
					</Tooltip>
					<Tooltip title={i18n.t("portal.components.eventBannerTSX.nextButton")}>
						<IconButton className={styles.navigationButton} onClick={handleNext}>
							<SvgIcon>
								<CaretDown />
							</SvgIcon>
						</IconButton>
					</Tooltip>
				</div>
			)}
			<div ref={wrapperRef} className={styles.centralWrapper}>
				<div className={styles.bannerScrollContainer}>
					{bannersToRender.map(({ index, type }) => (
						<BannerContent
							key={`${type}-${index}`}
							banner={banners[index]}
							type={type}
							direction={direction}
							showMultipleBanners={showMultipleBanners}
							wrapperRef={wrapperRef}
						/>
					))}
				</div>
			</div>
			{onClose ? (
				<IconButton
					className={styles.closeButton}
					onClick={onClose}
					aria-label={i18n.t("portal.components.eventBannerTSX.closeButtonAriaLabel")}
				>
					<SvgIcon>
						<Close />
					</SvgIcon>
				</IconButton>
			) : (
				<span className={styles.closeIcon}>
					<SvgIcon>
						<Close />
					</SvgIcon>
				</span>
			)}
		</div>
	);
}
